import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Sparkles, Save, PenLine } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { LANGS, POST_TYPES, errMsg, toLocalIST } from "./mk";
import { PosterMaker } from "./PosterMaker";
import { PostPreview } from "./PostPreview";
import { ReelBuilder } from "./ReelBuilder";
import { reelKey } from "./mkMedia";

const blankReel = (source) => ({ source, video_id: "", music_id: "", music_start: 0, music_end: 0, music_volume: 1, keep_original: true, original_volume: 1, duration: 15 });
const blank = (date) => ({ title: "", topic: "", language: "en", post_type: "Admission Open", platforms: ["instagram", "facebook"],
  caption: "", hashtagsText: "", image_path: "", scheduled_local: date || "", include_enquiry_link: true, format: "photo", reel: blankReel("photo") });

const fromPost = (p) => {
  const reel = p.reel ? { ...blankReel(p.reel.source), ...p.reel } : blankReel("photo");
  if (p.reel?.video_path && !reel.rendered_key) reel.rendered_key = reelKey(reel, p.image_path);
  return { ...blank(), ...p, reel, format: p.media_type === "reel" ? `reel-${reel.source}` : "photo", hashtagsText: (p.hashtags || []).join(" "), scheduled_local: toLocalIST(p.scheduled_at) };
};
const FORMATS = [{ v: "photo", l: "Photo post" }, { v: "reel-photo", l: "Photo + music (Reel)" }, { v: "reel-video", l: "Video Reel" }];

export default function PostEditor({ post, presetDate, onSaved }) {
  const [form, setForm] = useState(blank(presetDate));
  const [seed, setSeed] = useState(null);
  const [writing, setWriting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cache, setCache] = useState({});
  useEffect(() => { setForm(post ? fromPost(post) : blank(presetDate)); setSeed(null); setCache({}); }, [post, presetDate]);
  const setFormat = (v) => setForm((f) => ({ ...f, format: v, reel: v.startsWith("reel") ? { ...f.reel, source: v.slice(5) } : f.reel }));
  const isReel = form.format.startsWith("reel");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const togglePf = (pf) => set("platforms", form.platforms.includes(pf) ? form.platforms.filter((x) => x !== pf) : [...form.platforms, pf]);

  const write = async () => {
    if (!form.topic.trim()) { toast.error("Describe what the post is about"); return; }
    setWriting(true);
    try {
      const { data } = await api.post("/admin/marketing/generate-caption", { topic: form.topic, language: form.language, post_type: form.post_type });
      setForm((f) => ({ ...f, caption: data.caption, hashtagsText: (data.hashtags || []).join(" "), title: f.title || (data.poster_headline || f.topic).slice(0, 60) }));
      setSeed({ headline: data.poster_headline, details: data.poster_details });
      toast.success("Caption written");
    } catch (e) { toast.error(errMsg(e, "AI writer failed")); }
    finally { setWriting(false); }
  };

  const save = async () => {
    if (!form.platforms.length) { toast.error("Choose Instagram and/or Facebook"); return; }
    if (isReel && form.reel.video_path && form.reel.rendered_key !== reelKey(form.reel, form.image_path)) toast.warning("Reel settings changed — make the preview again before approval");
    setSaving(true);
    const body = { title: form.title || form.topic.slice(0, 60) || "Untitled post", topic: form.topic, language: form.language, post_type: form.post_type,
      platforms: form.platforms, caption: form.caption, image_path: form.image_path, include_enquiry_link: form.include_enquiry_link,
      hashtags: form.hashtagsText.split(/\s+/).filter(Boolean), scheduled_local: form.scheduled_local || null,
      media_type: isReel ? "reel" : "photo", reel: isReel ? form.reel : null };
    try {
      const { data } = post?.id ? await api.patch(`/admin/marketing/posts/${post.id}`, body) : await api.post("/admin/marketing/posts", body);
      toast.success(post?.id ? "Saved as draft — needs approval" : "Draft saved — send it for approval");
      onSaved(data);
    } catch (e) { toast.error(errMsg(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-6" data-testid="post-editor">
      <div className="space-y-5 min-w-0">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4">
          <div className="flex items-center gap-2"><PenLine className="h-4 w-4 text-blue-700" /><h3 className="font-bold text-slate-900">{post?.id ? "Edit post" : "AI Content Studio"}</h3></div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label className="text-xs">Post type</Label>
              <Select value={form.post_type} onValueChange={(v) => set("post_type", v)}><SelectTrigger data-testid="post-type"><SelectValue /></SelectTrigger>
                <SelectContent>{POST_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Language</Label>
              <Select value={form.language} onValueChange={(v) => set("language", v)}><SelectTrigger data-testid="post-language"><SelectValue /></SelectTrigger>
                <SelectContent>{LANGS.map((l) => <SelectItem key={l.v} value={l.v}>{l.l}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Post on</Label>
              <div className="flex gap-4 h-10 items-center">
                {["instagram", "facebook"].map((pf) => (
                  <label key={pf} className="flex items-center gap-1.5 text-sm capitalize"><Checkbox checked={form.platforms.includes(pf)} onCheckedChange={() => togglePf(pf)} data-testid={`post-platform-${pf}`} />{pf}</label>
                ))}
              </div></div>
          </div>
          <div><Label className="text-xs">Format</Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="post-format">
              {FORMATS.map((f) => (
                <button key={f.v} type="button" onClick={() => setFormat(f.v)} data-testid={`post-format-${f.v}`}
                  className={`rounded-xl border px-3 py-2 text-sm font-semibold transition-colors ${form.format === f.v ? "border-blue-800 bg-blue-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-blue-300"}`}>{f.l}</button>
              ))}
            </div>
            {form.format === "photo" && <p className="text-[11px] text-slate-500 mt-1">Instagram and Facebook don't allow music on photo posts published from outside their apps. Choose "Photo + music (Reel)" to add music.</p>}
          </div>
          <div><Label className="text-xs">What is this post about?</Label>
            <Textarea rows={3} value={form.topic} onChange={(e) => set("topic", e.target.value)} placeholder="e.g. Admissions open for Nursery to Std 10, small batches, experienced teachers, Khar East" data-testid="post-topic" /></div>
          <Button type="button" onClick={write} disabled={writing} className="bg-gradient-to-r from-blue-800 to-sky-600 text-white" data-testid="post-write-btn">
            {writing ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Writing…</> : <><Sparkles className="mr-1 h-4 w-4" /> Write caption with AI</>}
          </Button>
          <div><Label className="text-xs">Title (for your calendar)</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="post-title" /></div>
          <div><Label className="text-xs">Caption (edit freely)</Label><Textarea rows={7} value={form.caption} onChange={(e) => set("caption", e.target.value)} data-testid="post-caption" /></div>
          <div><Label className="text-xs">Hashtags (space separated)</Label><Input value={form.hashtagsText} onChange={(e) => set("hashtagsText", e.target.value)} data-testid="post-hashtags" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
            <div><Label className="text-xs">Planned date & time (India time)</Label><Input type="datetime-local" value={form.scheduled_local} onChange={(e) => set("scheduled_local", e.target.value)} data-testid="post-schedule-input" /></div>
            <label className="flex items-center gap-2 text-sm pb-2"><Switch checked={form.include_enquiry_link} onCheckedChange={(v) => set("include_enquiry_link", v)} data-testid="post-link-switch" /> Add admission enquiry link</label>
          </div>
        </div>
        {isReel && <ReelBuilder reel={form.reel} imagePath={form.image_path} onReel={(r) => set("reel", r)} cache={cache} setCache={setCache} />}
        {form.format !== "reel-video" && <PosterMaker language={form.language} seed={seed} value={form.image_path} onChange={(p) => set("image_path", p)} />}
      </div>
      <div className="space-y-4 min-w-0 xl:sticky xl:top-4 self-start">
        <PostPreview form={form} />
        <Button onClick={save} disabled={saving} className="w-full bg-blue-900 hover:bg-blue-950 h-11" data-testid="post-save-btn">
          {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />} Save as Draft
        </Button>
        <p className="text-xs text-slate-500 text-center">Nothing is posted until you approve it in Approvals.</p>
      </div>
    </div>
  );
}
